/**
 * Enhanced useApi Hook with client-side caching for improved performance
 * 
 * Function-based API hook that uses the unified feedback system,
 * provides essential retry and offline capabilities, and implements
 * client-side caching to reduce network requests.
 */
import { useState, useEffect, useCallback } from 'react';
import { useFeedback } from '@/shared/contexts/FeedbackContext';
import { createApiClient, safeApiCall, isOnline, handleOffline } from '@/services/api/client';
import { AxiosInstance } from 'axios';
import { ApiResponse } from '@/types/api';
import { useApiCache, CacheOptions } from './useApiCache';

/**
 * Return type for useApi hook
 */
export interface UseApiReturn<T> {
  /** API response containing data and error state */
  data: ApiResponse<T>;
  /** Loading state indicator */
  isLoading: boolean;
  /** Error object if request failed */
  error: Error | null;
  /** Offline state indicator */
  isOffline: boolean;
  /** Function to refetch data */
  refetch: () => Promise<void>;
  /** Function to retry failed request */
  retry: () => void;
  /** Function to invalidate cache */
  invalidateCache: () => void;
}

/**
 * API hook configuration options
 */
export interface UseApiOptions {
  /** Whether to fetch data immediately on mount */
  immediate?: boolean;
  /** Cache configuration options */
  cacheOptions?: CacheOptions & {
    /** Whether to enable caching (default: true) */
    enabled?: boolean;
    /** Unique identifier for the API endpoint */
    cacheKey?: string;
  };
}

/**
 * Default API hook options
 */
const DEFAULT_OPTIONS: UseApiOptions = {
  immediate: true,
  cacheOptions: {
    enabled: true,
    ttl: 5 * 60 * 1000, // 5 minutes
    useOffline: true,
    invalidateOnFocus: false,
    persist: false,
  },
};

/**
 * Generic API hook for data fetching with error handling, offline support, and caching
 * 
 * @template T - Type of data returned by the API
 * @param {(client: AxiosInstance) => Promise<T | null>} apiFunction - Function that makes the API call and returns raw data
 * @param {UseApiOptions | boolean} options - Configuration options or legacy immediate flag
 * @param {Record<string, unknown>} dependencies - Dependencies for the API call
 * @returns {UseApiReturn<T>} API state and control functions
 */
export function useApi<T>(
  apiFunction: (client: AxiosInstance) => Promise<T | null>,
  options: UseApiOptions | boolean = {},
  dependencies: Record<string, unknown> = {}
): UseApiReturn<T> {
  // Handle legacy boolean immediate parameter for backward compatibility
  const normalizedOptions: UseApiOptions = typeof options === 'boolean' 
    ? { immediate: options } 
    : options;
  
  const mergedOptions = {
    ...DEFAULT_OPTIONS,
    ...normalizedOptions,
    cacheOptions: {
      ...DEFAULT_OPTIONS.cacheOptions,
      ...(normalizedOptions.cacheOptions || {}),
    },
  };
  
  const [data, setData] = useState<ApiResponse<T>>({ data: null, error: null });
  const [isLoading, setIsLoading] = useState<boolean>(!!mergedOptions.immediate);
  const [error, setError] = useState<Error | null>(null);
  const [isOffline, setIsOffline] = useState<boolean>(!isOnline());
  const { addError } = useFeedback();
  
  // Initialize cache
  const {
    generateCacheKey,
    getFromCache,
    setInCache,
    invalidateCache: invalidateCacheEntry,
  } = useApiCache(mergedOptions.cacheOptions);
  
  // Create a stable cache key
  const cacheKey = useCallback(() => {
    const baseKey = mergedOptions.cacheOptions?.cacheKey || 'api_request';
    return generateCacheKey(baseKey, dependencies);
  }, [generateCacheKey, mergedOptions.cacheOptions?.cacheKey, dependencies]);
  
  // Create API client with error handling
  const apiClient = createApiClient(addError);
  
  // Function to invalidate cache
  const invalidateCache = useCallback(() => {
    invalidateCacheEntry(cacheKey());
  }, [invalidateCacheEntry, cacheKey]);
  
  // Function to fetch data
  const fetchData = useCallback(async (): Promise<void> => {
    // Check if offline
    if (!isOnline()) {
      setIsOffline(true);
      
      // Try to use cached data when offline if enabled
      if (mergedOptions.cacheOptions?.enabled && mergedOptions.cacheOptions?.useOffline) {
        const cachedData = getFromCache<T>(cacheKey());
        if (cachedData) {
          setData(cachedData);
          setIsLoading(false);
          return;
        }
      }
      
      handleOffline(addError);
      setIsLoading(false);
      return;
    }
    
    setIsOffline(false);
    
    // Check cache first if enabled
    if (mergedOptions.cacheOptions?.enabled) {
      const cachedData = getFromCache<T>(cacheKey());
      if (cachedData) {
        setData(cachedData);
        setIsLoading(false);
        return;
      }
    }
    
    // No cache hit, proceed with API call
    setIsLoading(true);
    setError(null);
    
    try {
      // Call the API function and get raw data
      const rawResult = await safeApiCall(() => apiFunction(apiClient), addError);
      
      // Wrap the raw result in an ApiResponse
      const apiResponse: ApiResponse<T> = {
        data: rawResult,
        error: rawResult === null ? new Error('API request failed') : null
      };
      
      setData(apiResponse);
      
      // Cache successful responses if enabled
      if (mergedOptions.cacheOptions?.enabled && !apiResponse.error) {
        setInCache<T>(cacheKey(), apiResponse);
      }
      
      if (apiResponse.error) {
        throw apiResponse.error;
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [
    apiFunction, 
    addError, 
    cacheKey, 
    getFromCache, 
    setInCache, 
    mergedOptions.cacheOptions?.enabled,
    mergedOptions.cacheOptions?.useOffline,
    ...Object.values(dependencies)
  ]);
  
  // Function to retry the API call
  const retry = useCallback((): void => {
    // Invalidate cache on retry
    if (mergedOptions.cacheOptions?.enabled) {
      invalidateCache();
    }
    fetchData();
  }, [fetchData, invalidateCache, mergedOptions.cacheOptions?.enabled]);
  
  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = (): void => {
      setIsOffline(false);
      if (error) {
        // Auto-retry when coming back online
        retry();
      }
    };
    
    const handleOfflineEvent = (): void => {
      setIsOffline(true);
      handleOffline(addError);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOfflineEvent);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOfflineEvent);
    };
  }, [error, retry, addError]);
  
  // Fetch data on mount if immediate is true
  useEffect(() => {
    if (mergedOptions.immediate) {
      fetchData();
    }
  }, [mergedOptions.immediate, fetchData]);
  
  return {
    data,
    isLoading,
    error,
    isOffline,
    refetch: fetchData,
    retry,
    invalidateCache,
  };
}

export default useApi;
